import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Table, Pagination, Form, Placeholder } from 'react-bootstrap';
import { Link, useHistory } from "react-router-dom";
import { FaCaretDown, FaCaretUp, FaExclamationCircle, FaEye, FaChartBar, FaList, FaTh } from "react-icons/fa";
import Select, { components } from 'react-select';
import ColorBullet from '../../ColorBullet';
import UserBullet from '../../UserBullet';
import styles from './TicketsTable.module.css';
import { colorOption, colorValueSingle, imageOption } from '../../SelectComponents';
import { baseURL } from '../../../helpers/baseURL';
import { headers } from '../../../helpers/Requests';
import { ucFirst } from '../../../helpers/generic';
import successAlert from '../../../helpers/successAlert';
import failAlert from '../../../helpers/failAlert';
import ActivityModal from './ActivityModal';

const LAYOUT_KEY = 'ticketsLayout';

const AssigneeValueContainer = ({ children, getValue, selectProps, ...props }) => {
	const selected = getValue();
	const isOpen = selectProps.menuIsOpen;
	const input = React.Children.toArray(children).pop();

	if (isOpen) {
		return <components.ValueContainer {...props} selectProps={selectProps}>{children}</components.ValueContainer>;
	}

	const users = selected.map(a => ({ id: a.value, name: a.label, profile_image: a.image }));

	if (users.length === 0) {
		return <components.ValueContainer {...props} selectProps={selectProps}>{children}</components.ValueContainer>;
	}

	return (
		<components.ValueContainer {...props} selectProps={selectProps}>
			<UserBullet user={users} size={25} tooltip={true} />
			{input}
		</components.ValueContainer>
	);
};

const selectStyles = {
	control: (base, state) => ({
		...base,
		minHeight: '28px',
		// Lock to single-row height only when closed (avatars fit in one line)
		// When open, let it grow so selected chip names are fully visible
		...(state.menuIsOpen ? {} : { height: '28px', flexWrap: 'nowrap' }),
		fontSize: '13px',
		border: state.isDisabled ? '1px solid transparent' : '1px solid #aaa',
		boxShadow: 'none',
		background: state.isDisabled ? 'rgba(0,0,0,0.04)' : 'transparent',
		cursor: state.isDisabled ? 'not-allowed' : 'pointer',
		opacity: state.isDisabled ? 0.6 : 1,
		pointerEvents: state.isDisabled ? 'none' : undefined,
		'&:hover': { borderColor: '#aaa' },
	}),
	dropdownIndicator: (base, state) => ({
		...base,
		padding: '0 2px',
		opacity: state.isDisabled ? 0.2 : 0.4,
	}),
	clearIndicator: () => ({
		display: 'none',
		pointerEvents: 'none',
	}),
	valueContainer: (base, state) => ({
		...base,
		padding: '0 4px',
		// Closed: clip overflow so avatars never push height; Open: show all chips
		...(state.selectProps.menuIsOpen
			? { flexWrap: 'wrap' }
			: { flexWrap: 'nowrap', overflow: 'hidden', height: '26px', alignItems: 'center' }
		),
	}),
	indicatorsContainer: (base, state) => ({
		...base,
		// Keep indicator anchored to top when open and control grows tall
		...(state.selectProps.menuIsOpen ? { alignSelf: 'flex-start', paddingTop: '1px' } : { height: '28px' }),
	}),
	indicatorSeparator: () => ({ display: 'none' }),
	menu: (base) => ({
		...base,
		zIndex: 9999,
		minWidth: '180px',
		fontSize: '13px',
	}),
	option: (base) => ({
		...base,
		fontSize: '13px',
	}),
};

/** Same look as normal cells, but non-interactive (no faint disabled styling). */
const readOnlySelectStyles = {
	...selectStyles,
	control: (base) => ({
		...base,
		minHeight: '28px',
		height: '28px',
		flexWrap: 'nowrap',
		fontSize: '13px',
		border: '1px solid transparent',
		boxShadow: 'none',
		background: 'transparent',
		cursor: 'default',
		opacity: 1,
		pointerEvents: 'none',
	}),
	singleValue: (base) => ({
		...base,
		color: 'inherit',
		opacity: 1,
	}),
	placeholder: (base) => ({
		...base,
		color: 'inherit',
		opacity: 1,
	}),
	dropdownIndicator: () => ({ display: 'none' }),
	indicatorsContainer: () => ({ display: 'none' }),
	clearIndicator: () => ({ display: 'none' }),
};

const TicketsTable = ({ tickets, unseenTickets, total, columns, sort, pagination, selection, isLoading, onSortChange, onPaginationChange, onSelectionChange, isCustomer, data, filters, onTicketUpdate, readOnly = false }) => {

	const embeddedColumns = ['description', 'tags'];
	const unSortableColumns = ['description', 'tags', 'transferred_to', 'closed_by'];
	const dispatch = useDispatch();
	const history = useHistory();
	const [isAllSelected, setIsAllSelected] = useState(false);
	const [updatingCell, setUpdatingCell] = useState({});
	const [activityModal, setActivityModal] = useState(null);
	const [layout, setLayout] = useState(() => {
		try { return localStorage.getItem(LAYOUT_KEY) || 'list'; } catch { return 'list'; }
	});

	const toggleLayout = (val) => {
		setLayout(val);
		try { localStorage.setItem(LAYOUT_KEY, val); } catch {}
	};

	const totalPages = Math.ceil(total / pagination.perPage);
	const current = pagination.page;

	const getPageNumbers = () => {
		const pages = [];
		const maxVisible = 16;
		let start = Math.max(1, current - Math.floor(maxVisible / 2));
		let end = start + maxVisible - 1;
		if (end > totalPages) { end = totalPages; start = Math.max(1, end - maxVisible + 1); }
		if (start > 1) {
			pages.push(<Pagination.Item key={1} onClick={() => onPaginationChange(1, pagination.perPage)}>1</Pagination.Item>);
			if (start > 2) pages.push(<Pagination.Ellipsis key="start-ellipsis" />);
		}
		for (let i = start; i <= end; i++) {
			pages.push(
				<Pagination.Item key={i} active={i === current} onClick={() => onPaginationChange(i, pagination.perPage)}>
					{i}
				</Pagination.Item>
			);
		}
		if (end < totalPages) {
			if (end < totalPages - 1) pages.push(<Pagination.Ellipsis key="end-ellipsis" />);
			pages.push(<Pagination.Item key={totalPages} onClick={() => onPaginationChange(totalPages, pagination.perPage)}>{totalPages}</Pagination.Item>);
		}
		return pages;
	};

	const pageNumbers = getPageNumbers();

	const setSortIcon = (by) => {
		return sort.by === by ? (sort.direction === 'asc' ? <FaCaretUp /> : <FaCaretDown />) : null;
	};

	const requestSort = (by) => {
		if (!by || unSortableColumns.includes(by)) return;
		onSortChange(by);
	};

	const handleSelectAll = (e) => {
		const checked = e.target.checked;
		const IDs = tickets.filter(ticket => !ticket.deleted).map(ticket => ticket.id);
		onSelectionChange(IDs, checked);
	};

	const handleSelectSingle = (id, checked) => {
		onSelectionChange([id], checked);
	};

	useEffect(() => {
		setIsAllSelected(tickets && tickets.every(ticket => selection.includes(ticket.id)));
	}, [tickets, selection]);

	const buildColorOptions = (items) => {
		if (!items) return [];
		return items.map(item => ({ value: item.id, label: ucFirst(item.name), color: item.color }));
	};

	const buildUserOptions = (items) => {
		if (!items) return [];
		return items.map(item => ({ value: item.id, label: ucFirst(item.name), image: item.profile_image, description: item.email }));
	};


	const handleInlineUpdate = async (ticketId, field, selectedValue) => {
		const key = `${ticketId}-${field}`;
		setUpdatingCell(prev => ({ ...prev, [key]: true }));
		try {
			const updateData = field === 'assignees'
				? (selectedValue || []).map(v => v.value)
				: (selectedValue?.value ?? null);
			const response = await axios.patch(`${baseURL}ticket-update/${ticketId}`, { [field]: updateData }, headers);
			const updatedTicket = response.data.ticket;
			const patchData = {};
			if ('status' in updatedTicket)     patchData.status     = updatedTicket.status;
			if ('department' in updatedTicket) patchData.department = updatedTicket.department;
			if ('priority' in updatedTicket)   patchData.priority   = updatedTicket.priority;
			// The edit just moved the ticket's updated_at, so refresh the cell in place
			// instead of leaving the old stamp until the next full reload.
			if ('last_update_at' in updatedTicket) patchData.last_update_at = updatedTicket.last_update_at;
			if ('assignees' in updatedTicket) {
				const raw = updatedTicket.assignees || [];
				const userMap = Object.fromEntries((data?.users ?? []).map(u => [u.id, u]));
				patchData.assignees = raw.map(entry => {
					if (entry && typeof entry === 'object' && entry.name) {
						return { id: entry.id, name: entry.name, email: entry.email, profile_image: entry.profile_image };
					}
					const id = entry?.id ?? entry;
					const u = userMap[id];
					return u ? { id: u.id, name: u.name, email: u.email, profile_image: u.profile_image } : null;
				}).filter(Boolean);
			}
			const updatedStatusId = typeof updatedTicket.status === 'object'
				? updatedTicket.status?.id
				: updatedTicket.status;
			const updatedStatusName = (data?.statuses ?? [])
				.find(s => s.id === updatedStatusId)?.name?.toLowerCase();
			const activeStatusFilter = filters?.status?.toLowerCase?.();
			let shouldRemove = false;
			if (field === 'status') {
				if (!activeStatusFilter) { shouldRemove = ['closed', 'rejected'].includes(updatedStatusName); }
				else { shouldRemove = updatedStatusName !== activeStatusFilter; }
			}
			if (shouldRemove) {
				dispatch({ type: 'REMOVE_TICKET', payload: { id: ticketId } });
			} else {
				dispatch({ type: 'PATCH_TICKET', payload: { id: ticketId, data: patchData } });
			}
			successAlert('Ticket updated');
		} catch (e) {
			console.error('Inline ticket update failed', e);
			failAlert(e.response?.data?.message || 'Something went wrong. Please try again later.');
		} finally {
			setUpdatingCell(prev => ({ ...prev, [key]: false }));
		}
	};

	const StatusSelect = ({ ticket, disabled = false }) => {
		const allOptions = buildColorOptions(data?.statuses);
		// Overdue is set automatically based on due_date — never a manual
		// choice. Still shown as the selected value if the ticket already
		// is Overdue, just not offered as something to switch to.
		const options = allOptions.filter(o => o.label?.toLowerCase() !== 'overdue');
		const value = allOptions.find(o => o.value === ticket.status?.id) || null;
		return (
			<Select options={options} value={value} components={{ Option: colorOption, SingleValue: colorValueSingle }}
				onChange={(val) => handleInlineUpdate(ticket.id, 'status', val)}
				styles={disabled ? readOnlySelectStyles : selectStyles} menuPortalTarget={document.body} menuPosition="fixed"
				isDisabled={!disabled && !!updatingCell[`${ticket.id}-status`]} classNamePrefix="inline-select" placeholder="-" />
		);
	};

	const prioritySelectStyles = {
		...selectStyles,
		clearIndicator: (base, state) => ({
			...base, padding: '0 2px', opacity: state.isDisabled ? 0.2 : 0.6, cursor: 'pointer',
		}),
	};

	const PrioritySelect = ({ ticket, disabled = false }) => {
		const options = buildColorOptions(data?.priorities);
		const value = options.find(o => o.value === ticket.priority?.id) || null;
		return (
			<Select options={options} value={value} isClearable components={{ Option: colorOption, SingleValue: colorValueSingle }}
				onChange={(val) => handleInlineUpdate(ticket.id, 'priority', val)}
				styles={disabled ? readOnlySelectStyles : prioritySelectStyles} menuPortalTarget={document.body} menuPosition="fixed"
				isDisabled={!disabled && !!updatingCell[`${ticket.id}-priority`]} classNamePrefix="inline-select" placeholder="-" />
		);
	};

	const DepartmentSelect = ({ ticket, disabled = false }) => {
		const localOptions = buildColorOptions(data?.departments);
		const dept = ticket.department;
		// Destination-workspace department IDs won't be in the source workspace list.
		const knownIds = new Set(localOptions.map(o => o.value));
		const foreignOption = dept && dept.id != null && !knownIds.has(dept.id)
			? { value: dept.id, label: ucFirst(dept.name || `#${dept.id}`), color: dept.color }
			: null;
		const options = foreignOption ? [...localOptions, foreignOption] : localOptions;
		const value = options.find(o => o.value === dept?.id) || null;

		if (disabled) {
			return value
				? <ColorBullet color={value.color} text={value.label} size={12} />
				: (dept ? <ColorBullet color={dept.color} text={dept.name} size={12} /> : null);
		}

		return (
			<Select options={options} value={value} components={{ Option: colorOption, SingleValue: colorValueSingle }}
				onChange={(val) => handleInlineUpdate(ticket.id, 'department', val)}
				styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed"
				isDisabled={!!updatingCell[`${ticket.id}-department`]} classNamePrefix="inline-select" placeholder="-" />
		);
	};

	const resolveTicketAssignees = (ticket) => {
		return (ticket.assignees || []).map(a => {
			if (a && typeof a === 'object' && a.id != null) return a;
			const local = (data?.users || []).find(u => u.id === a);
			return local || (a != null ? { id: a, name: `#${a}` } : null);
		}).filter(Boolean);
	};

	const AssigneeSelect = ({ ticket, disabled = false }) => {
		const ticketAssignees = resolveTicketAssignees(ticket);
		// Read-only: render avatars directly so hover tooltips work (Select uses pointer-events: none).
		if (disabled) {
			return ticketAssignees.length > 0
				? <UserBullet user={ticketAssignees} size={25} tooltip={true} />
				: null;
		}
		const localOptions = buildUserOptions(data?.users);
		const knownIds = new Set(localOptions.map(o => o.value));
		const foreignOptions = ticketAssignees
			.filter(a => !knownIds.has(a.id))
			.map(a => ({ value: a.id, label: ucFirst(a.name || `#${a.id}`), image: a.profile_image, description: a.email }));
		const options = [...localOptions, ...foreignOptions];
		const assigneeIds = ticketAssignees.map(a => a.id);
		const value = options.filter(o => assigneeIds.includes(o.value));
		return (
			<Select isMulti options={options} value={value}
				components={{ Option: imageOption, ValueContainer: AssigneeValueContainer }}
				onChange={(val) => handleInlineUpdate(ticket.id, 'assignees', val)}
				styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed"
				isDisabled={!!updatingCell[`${ticket.id}-assignees`]} classNamePrefix="inline-select" placeholder="-"
				filterOption={(option, input) =>
					option.label?.toLowerCase().includes(input.toLowerCase()) ||
					option.data?.description?.toLowerCase().includes(input.toLowerCase())} />
		);
	};


	/* ── Layout toggle shared UI ── */
	const LayoutToggle = () => (
		<div className={`d-inline-flex ${styles.layoutToggleGroup}`}>
			<button
				type="button"
				className={`${styles.actionBtn} ${layout === 'list' ? styles.actionBtnActive : ''}`}
				onClick={() => toggleLayout('list')}
				title="List View"
			>
				<FaList size={13} />
			</button>
			<button
				type="button"
				className={`${styles.actionBtn} ${layout === 'grid' ? styles.actionBtnActive : ''}`}
				onClick={() => toggleLayout('grid')}
				title="Grid View"
			>
				<FaTh size={13} />
			</button>
		</div>
	);

	/* ── Skeleton loading state ── */
	if (isLoading) {
		const skeletonRows = 8;
		const visibleColumns = Object.entries(columns).filter(
			([key, value]) => !embeddedColumns.includes(key) && value.visible && (key !== 'user' || !isCustomer)
		);
		return (
			<div className={styles.tableWrapper}>
				<Table className={styles.table}>
					<thead>
						<tr>
							{!readOnly && <th className={`${styles.checkboxCell}${isCustomer ? ` ${styles.noSelect}` : ''}`} />}
							{visibleColumns.map(([key, value]) => (
								<th key={key} className={key === 'title' ? styles.titleCell : undefined}>{value.label}</th>
							))}
							<th className={styles.stickyActions} style={{ width: 110, textAlign: 'center' }}>Actions</th>
						</tr>
					</thead>
					<tbody>
						{Array.from({ length: skeletonRows }).map((_, rowIdx) => (
							<tr key={rowIdx}>
								{!readOnly && (
									<td className={styles.checkboxCell}>
										<Placeholder as="span" animation="glow">
											<Placeholder style={{ width: 16, height: 16, borderRadius: 3, display: 'block' }} />
										</Placeholder>
									</td>
								)}
								{visibleColumns.map(([key], colIdx) => (
									<td key={colIdx} className={key === 'title' ? styles.titleCell : styles.selectCell}>
										{key === 'title' ? (
											<Placeholder as="div" animation="glow">
												<Placeholder xs={7} className="mb-1" style={{ display: 'block', borderRadius: 4 }} />
												<Placeholder xs={5} size="sm" style={{ display: 'block', borderRadius: 4 }} />
											</Placeholder>
										) : (
											<Placeholder as="div" animation="glow">
												<Placeholder xs={8} style={{ display: 'block', borderRadius: 4 }} />
											</Placeholder>
										)}
									</td>
								))}
								<td className={styles.stickyActions} style={{ textAlign: 'center' }}>
									<div style={{ display: 'inline-flex', gap: 5 }}>
										<Placeholder.Button variant="secondary" style={{ width: 26, height: 26, padding: 0, borderRadius: 5 }} aria-hidden="true" />
										<Placeholder.Button variant="secondary" style={{ width: 26, height: 26, padding: 0, borderRadius: 5 }} aria-hidden="true" />
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</Table>
			</div>
		);
	}

	if (tickets === undefined || tickets.length === 0) {
		return <div className="labels-container-div justify-content-center">No Tickets Found</div>;
	}


	/* ── Grid layout ── */
	if (layout === 'grid') {
		return (
			<>
				{activityModal && (
					<ActivityModal ticketId={activityModal} onClose={() => setActivityModal(null)} users={data?.users || []} />
				)}

				<div className="d-flex justify-content-end align-items-center mb-2">
					<LayoutToggle />
				</div>

				<div className="row row-cols-1 row-cols-sm-2 row-cols-lg-4 g-2 mb-3">
					{tickets.map((ticket) => (
						<div key={ticket.id} className="col">
							<div className="card h-100">
								<div className="card-body d-flex flex-column gap-2 p-2 text-start">

									{/* Header: checkbox + title */}
									<div className="d-flex align-items-center gap-2">
										{!isCustomer && !readOnly && (
											<Form.Check
												className="mt-1 flex-shrink-0"
												disabled={ticket.deleted}
												checked={selection.includes(ticket.id)}
												onChange={(e) => handleSelectSingle(ticket.id, e.target.checked)}
											/>
										)}
										<Link className={`${styles.link} fw-medium text-truncate small`} to={`/ticket/${ticket.id}`}>
											{ticket.title}
										</Link>
										{(ticket.was_transferred_in || ticket.is_transferred_out) ? (
											<span className={`badge ${styles.transferredBadge}`}>Transferred</span>
										) : null}
									</div>

									{/* Tags */}
									{columns.tags?.visible && ticket.tags?.length > 0 && (
										<div className="d-flex flex-wrap gap-1">
											{ticket.tags.map((tag, i) => (
												<span key={i} className="badge" style={{ backgroundColor: tag.color, fontSize: 10 }}>{tag.name}</span>
											))}
										</div>
									)}

									{/* Description */}
									{ticket.description && (
										<p className="text-muted mb-0" style={{ fontSize: 12, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
											{ticket.description}
										</p>
									)}

									{/* Meta */}
									<div className="d-flex flex-column gap-1 flex-grow-1">
										{columns.status?.visible && (
											<div className="d-flex align-items-center gap-2">
												<span className="text-muted" style={{ fontSize: 11, minWidth: 52 }}>Status</span>
												<div className="flex-grow-1">
													{data?.statuses?.length > 0
														? <StatusSelect ticket={ticket} disabled={readOnly} />
														: ticket.status && <ColorBullet color={ticket.status.color} text={ticket.status.name} size={12} />}
												</div>
											</div>
										)}
										{columns.priority?.visible && (
											<div className="d-flex align-items-center gap-2">
												<span className="text-muted" style={{ fontSize: 11, minWidth: 52 }}>Priority</span>
												<div className="flex-grow-1">
													{data?.priorities?.length > 0
														? <PrioritySelect ticket={ticket} disabled={readOnly} />
														: ticket.priority && <ColorBullet color={ticket.priority.color} text={ticket.priority.name} size={12} />}
												</div>
											</div>
										)}
										{columns.department?.visible && (
											<div className="d-flex align-items-center gap-2">
												<span className="text-muted" style={{ fontSize: 11, minWidth: 52 }}>Dept</span>
												<div className="flex-grow-1">
													{data?.departments?.length > 0
														? <DepartmentSelect ticket={ticket} disabled={readOnly} />
														: ticket.department && <ColorBullet color={ticket.department.color} text={ticket.department.name} size={12} />}
												</div>
											</div>
										)}
										{columns.user?.visible && !isCustomer && (
											<div className="d-flex align-items-center gap-2">
												<span className="text-muted" style={{ fontSize: 11, minWidth: 52 }}>Assigned</span>
												<div className="flex-grow-1">
													{data?.users?.length > 0
														? <AssigneeSelect ticket={ticket} disabled={readOnly} />
														: ticket.assignees?.length > 0 && <UserBullet user={ticket.assignees} />}
												</div>
											</div>
										)}
										{columns.transferred_to?.visible && (
											<div className="d-flex align-items-center gap-2">
												<span className="text-muted" style={{ fontSize: 11, minWidth: 52 }}>To</span>
												<span style={{ fontSize: 12 }}>{ticket.transfer_info?.to_workspace_name || '-'}</span>
											</div>
										)}
										{columns.type?.visible && ticket.type && (
											<div className="d-flex align-items-center gap-2">
												<span className="text-muted" style={{ fontSize: 11, minWidth: 52 }}>Type</span>
												<div className="flex-grow-1">
													<ColorBullet color={ticket.type.color} text={ticket.type.name} size={12} />
												</div>
											</div>
										)}
										{columns.replies?.visible && (
											<div className="d-flex align-items-center gap-2">
												<span className="text-muted" style={{ fontSize: 11, minWidth: 52 }}>Replies</span>
												<span style={{ fontSize: 12 }}>{ticket.replies ?? '-'}</span>
											</div>
										)}
										{columns.replied_at?.visible && (
											<div className="d-flex align-items-center gap-2">
												<span className="text-muted" style={{ fontSize: 11, minWidth: 52 }}>Replied</span>
												<span style={{ fontSize: 12 }}>{ticket.replied_at || '-'}</span>
											</div>
										)}
										{columns.due_date?.visible && (
											<div className="d-flex align-items-center gap-2">
												<span className="text-muted" style={{ fontSize: 11, minWidth: 52 }}>Due</span>
												<span style={{ fontSize: 12 }}>{ticket.due_date || '-'}</span>
											</div>
										)}
										{columns.last_update_at?.visible && (
											<div className="d-flex align-items-center gap-2">
												<span className="text-muted" style={{ fontSize: 11, minWidth: 52 }}>Updated</span>
												<span style={{ fontSize: 12 }}>{ticket.last_update_at || '-'}</span>
											</div>
										)}
										{columns.closed_at?.visible && (
											<div className="d-flex align-items-center gap-2">
												<span className="text-muted" style={{ fontSize: 11, minWidth: 52 }}>Closed</span>
												<span style={{ fontSize: 12 }}>{ticket.closed_at || '-'}</span>
											</div>
										)}
										{columns.created_by?.visible && (
											<div className="d-flex align-items-center gap-2">
												<span className="text-muted" style={{ fontSize: 11, minWidth: 52 }}>By</span>
												<span style={{ fontSize: 12 }}>{ticket.created_by || '-'}</span>
											</div>
										)}
										{columns.closed_by?.visible && (
											<div className="d-flex align-items-center gap-2">
												<span className="text-muted" style={{ fontSize: 11, minWidth: 52 }}>Closed by</span>
												<span style={{ fontSize: 12 }}>{ticket.closed_by || '-'}</span>
											</div>
										)}
									</div>
								</div>

								{/* Card Footer */}
								<div className="card-footer d-flex justify-content-between align-items-center py-1 px-2 text-start">
									<span className="text-muted" style={{ fontSize: 11 }}>{ticket.created_at || '-'}</span>
									<div className="d-flex gap-1">
										<button title="Activity" onClick={() => setActivityModal(ticket.id)} className={styles.actionBtn}>
											<FaChartBar size={11} />
										</button>
										<Link to={`/ticket/${ticket.id}`} style={{ textDecoration: 'none' }}>
											<button title="View Ticket" className={styles.actionBtn}><FaEye size={11} /></button>
										</Link>
									</div>
								</div>
							</div>
						</div>
					))}
				</div>

				{/* Pagination */}
				<div className={styles.tableFooter}>
					<div className="d-flex align-items-center flex-wrap" style={{ gap: 10 }}>
						<Pagination className={styles.pagination}>
							<Pagination.Prev disabled={current === 1} onClick={() => onPaginationChange(current - 1, pagination.perPage)} />
							{pageNumbers}
							<Pagination.Next disabled={current === totalPages} onClick={() => onPaginationChange(current + 1, pagination.perPage)} />
						</Pagination>
						<label htmlFor="perPageSelectGrid" className={styles.perPageLabel}>Page Size:</label>
						<Form.Control as="select" id="perPageSelectGrid" value={pagination.perPage}
							onChange={(e) => onPaginationChange(1, parseInt(e.target.value))}
							className={styles.perPage} style={{ width: 80 }}>
							{[10, 20, 50, 100].map((size) => <option key={size} value={size}>{size}</option>)}
						</Form.Control>
						<span className={styles.results}>Showing {tickets.length} of {total} Tickets</span>
					</div>
				</div>
			</>
		);
	}


	/* ── List layout (default) ── */
	return (
	<>
		{activityModal && (
			<ActivityModal ticketId={activityModal} onClose={() => setActivityModal(null)} users={data?.users || []} />
		)}

		{/* Toolbar with layout toggle */}
		<div className="d-flex justify-content-end mb-2">
			<LayoutToggle />
		</div>

		{/* TABLE (SCROLLABLE) */}
		<div className={styles.tableWrapper}>
			<Table className={styles.table} hover>
				<thead>
					<tr>
						{!readOnly && (
							<th className={`${styles.checkboxCell}${isCustomer ? ` ${styles.noSelect}` : ''}`}>
								{!isCustomer && <Form.Check checked={isAllSelected} onChange={handleSelectAll} />}
							</th>
						)}
						{Object.entries(columns).map(([column, value], index) => {
							const showColumn = value.visible && (column !== 'user' || !isCustomer);
							const sortable = !unSortableColumns.includes(column);
							return (!embeddedColumns.includes(column) && showColumn) && (
								<th
									key={index}
									className={`${sortable ? 'cursor-pointer' : ''}${column === 'title' ? ` ${styles.titleCell}` : ''}`}
									onClick={() => sortable && requestSort(column)}
								>
									{value.label}{sortable && setSortIcon(column)}
								</th>
							);
						})}
						<th className={styles.stickyActions} style={{ width: 110, textAlign: 'center' }}>Actions</th>
					</tr>
				</thead>

				<tbody>
					{tickets.map((ticket) => (
						<tr key={ticket.id}>
							{!readOnly && (
								<td className={styles.checkboxCell}>
									{!isCustomer && (
										<Form.Check
											disabled={ticket.deleted}
											checked={selection.includes(ticket.id)}
											onChange={(e) => handleSelectSingle(ticket.id, e.target.checked)}
										/>
									)}
								</td>
							)}
							{columns.title.visible && <td className={styles.titleCell}>
								<Link className={styles.titleLink} to={`/ticket/${ticket.id}`}>
									<div className={styles.titleBlock}>
										<div className={styles.titleText}>{ticket.title}</div>
										<div className={styles.description}>
											{columns.tags.visible && <span>{ticket.tags.map((tag, index) => (
												<span key={index} className="badge" style={{ backgroundColor: tag.color }}>{tag.name}</span>
											))}</span>}
											{ticket.description}
										</div>
									</div>
									{(ticket.was_transferred_in || ticket.is_transferred_out) ? (
										<span className={`badge ${styles.transferredBadge}`}>Transferred</span>
									) : null}
								</Link>
							</td>}
							{columns.status.visible && <td className={styles.selectCell}>
								{data?.statuses?.length > 0
									? <StatusSelect ticket={ticket} disabled={readOnly} />
									: ticket.status && <ColorBullet color={ticket.status.color} text={ticket.status.name} size={12} />}
							</td>}
							{columns.department.visible && <td className={styles.selectCell}>
								{data?.departments?.length > 0
									? <DepartmentSelect ticket={ticket} disabled={readOnly} />
									: ticket.department && <ColorBullet color={ticket.department.color} text={ticket.department.name} size={12} />}
							</td>}
							{columns.type.visible && <td>
								{ticket.type && <ColorBullet color={ticket.type.color} text={ticket.type.name} size={12} />}
							</td>}
							{columns.priority.visible && <td className={styles.selectCell}>
								{data?.priorities?.length > 0
									? <PrioritySelect ticket={ticket} disabled={readOnly} />
									: ticket.priority && <ColorBullet color={ticket.priority.color} text={ticket.priority.name} size={12} />}
							</td>}
							{columns.user.visible && !isCustomer && <td className={styles.selectCell}>
								{data?.users?.length > 0
									? <AssigneeSelect ticket={ticket} disabled={readOnly} />
									: ticket.assignees?.length > 0 && <UserBullet user={ticket.assignees} />}
							</td>}
							{columns.transferred_to?.visible && <td>{ticket.transfer_info?.to_workspace_name || '-'}</td>}
							{columns.replies.visible && <td>{ticket.replies}</td>}
							{columns.replied_at.visible && <td>{ticket.replied_at ? ticket.replied_at : '-'}</td>}
							{columns.due_date.visible && <td>{ticket.due_date ? ticket.due_date : '-'}</td>}
							{columns.created_at.visible && <td>{ticket.created_at ? ticket.created_at : '-'}</td>}
							{columns.last_update_at?.visible && <td>{ticket.last_update_at ? ticket.last_update_at : '-'}</td>}
							{columns.closed_at?.visible && <td>{ticket.closed_at ? ticket.closed_at : '-'}</td>}
							{columns.created_by?.visible && <td>{ticket.created_by || '-'}</td>}
							{columns.closed_by?.visible && <td>{ticket.closed_by || '-'}</td>}

							<td className={styles.stickyActions} style={{ textAlign: 'center' }}>
								<div style={{ display: 'inline-flex', gap: 5 }}>
									<button title="Activity" onClick={() => setActivityModal(ticket.id)} className={styles.actionBtn}>
										<FaChartBar size={11} />
									</button>
									<Link to={`/ticket/${ticket.id}`} style={{ textDecoration: 'none' }}>
										<button title="View Ticket" className={styles.actionBtn}><FaEye size={11} /></button>
									</Link>
								</div>
							</td>
						</tr>
					))}
				</tbody>
			</Table>
		</div>

		<div className={styles.tableFooter}>
			<div className="d-flex align-items-center flex-wrap" style={{ gap: 10 }}>
				<Pagination className={styles.pagination}>
					<Pagination.Prev disabled={current === 1} onClick={() => onPaginationChange(current - 1, pagination.perPage)} />
					{pageNumbers}
					<Pagination.Next disabled={current === totalPages} onClick={() => onPaginationChange(current + 1, pagination.perPage)} />
				</Pagination>
				<label htmlFor="perPageSelect" className={styles.perPageLabel}>Page Size:</label>
				<Form.Control as="select" id="perPageSelect" value={pagination.perPage}
					onChange={(e) => onPaginationChange(1, parseInt(e.target.value))}
					className={styles.perPage} style={{ width: 80 }}>
					{[10, 20, 50, 100].map((size) => <option key={size} value={size}>{size}</option>)}
				</Form.Control>
				<span className={styles.results}>Showing {tickets.length} of {total} Tickets</span>
			</div>
		</div>
	</>
);
};

export default TicketsTable;
